import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { EquipmentFormData, PopulatedEquipment, Athlete } from '../../types/mongodb';
import { athleteAPI } from '../../services/api';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';

interface EquipmentFormProps {
  onClose: () => void;
  onSubmit: (equipment: EquipmentFormData) => void;
  equipmentType?: 'suit' | 'skis' | 'shoes' | 'bindings';
  editingEquipment?: PopulatedEquipment | null;
  athleteId?: string; // Optional prop to auto-fill and lock athlete selection
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 3rem;
  animation: fadeIn 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: center;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: var(--bg-sidebar);
  padding: 2rem 2rem 1.75rem;
  border-radius: 1rem;
  width: 90%;
  max-width: 440px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    padding: 1.5rem 1.25rem 1.5rem;
    max-height: 90vh;
    overflow-y: auto;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h2 {
    font-size: 1.25rem;
    margin: 0 0 1.25rem 0;
    font-family: 'Poppins', sans-serif;
    color: var(--accent-orange);
    font-weight: 900;
    text-align: left;
    letter-spacing: -0.02em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  position: relative;
`;

const Label = styled.label`
  font-weight: 700;
  color: var(--text-gray);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.1rem;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input.attrs({
  className: 'equipment-form-input'
})`
  padding: 0.6rem 0.9rem;
  background: #fff !important;
  border: 1px solid var(--card-border, #e0e6ed) !important;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.15);
  }

  &::placeholder {
    color: var(--text-muted) !important;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: #e8e8e8 !important;
  }
`;

const Select = styled.select.attrs({
  className: 'equipment-form-select'
})`
  padding: 0.6rem 0.9rem;
  background: #fff !important;
  border: 1px solid var(--card-border, #e0e6ed) !important;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, box-shadow 0.2s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  min-height: 2.5rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: #e8e8e8 !important;
  }

  option {
    background: #fff;
    color: var(--text-gray);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: stretch;
    gap: 0.75rem;
    margin-top: 1.25rem;
    flex-wrap: wrap;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: ${props => props.$variant === 'primary' ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? '#fff' : 'var(--accent-orange)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  white-space: nowrap;
  box-shadow: ${props => props.$variant === 'primary' ? '0 2px 8px rgba(255, 150, 0, 0.3)' : 'none'};

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    padding: 0.6rem 1rem;
    min-height: 2.75rem;
  }

  &:hover {
    background: ${props => props.$variant === 'primary' ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
    color: #fff;
    border-color: ${props => props.$variant === 'primary' ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
    box-shadow: ${props => props.$variant === 'primary' ? '0 4px 12px rgba(255, 150, 0, 0.35)' : 'none'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  span {
    position: relative;
    z-index: 1;
  }
`;

const EquipmentForm: React.FC<EquipmentFormProps> = ({ onClose, onSubmit, equipmentType, editingEquipment, athleteId: filterAthleteId }) => {
  const [formData, setFormData] = useState<EquipmentFormData>({
    type: equipmentType || 'suit',
    name: '',
    brand: '',
    size: '',
    athleteId: filterAthleteId || ''
  });
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [isLoadingAthletes, setIsLoadingAthletes] = useState(true);

  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        setIsLoadingAthletes(true);
        const athleteData = await athleteAPI.getAll();
        setAthletes(athleteData);
      } catch (err) {
        console.error('Error fetching athletes:', err);
      } finally {
        setIsLoadingAthletes(false);
      }
    };

    fetchAthletes();
  }, []);

  useEffect(() => {
    if (editingEquipment) {
      setFormData({
        type: editingEquipment.type,
        name: editingEquipment.name,
        brand: editingEquipment.brand || '',
        size: editingEquipment.size || '',
        athleteId: editingEquipment.athleteId?._id || ''
      });
    } else {
      setFormData({
        type: equipmentType || 'suit',
        name: '',
        brand: '',
        size: '',
        athleteId: filterAthleteId || ''
      });
    }
  }, [editingEquipment, equipmentType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const getEquipmentTypeLabel = (type: string) => {
    switch (type) {
      case 'suit': return 'Suit';
      case 'skis': return 'Skis';
      case 'shoes': return 'Shoes';
      case 'bindings': return 'Bindings';
      default: return 'Equipment';
    }
  };

  const isEditing = !!editingEquipment;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()} data-equipment-form>
        <h2>{isEditing ? 'Edit' : 'Add New'} {getEquipmentTypeLabel(formData.type)}</h2>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="type">Type</Label>
            <Select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              disabled={isEditing}
            >
              <option value="suit">Suit</option>
              <option value="skis">Skis</option>
              <option value="shoes">Shoes</option>
              <option value="bindings">Bindings</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="athleteId">Assigned Athlete {filterAthleteId ? '(Auto-assigned)' : '(Optional)'}</Label>
            <Select
              id="athleteId"
              name="athleteId"
              value={formData.athleteId}
              onChange={handleChange}
              disabled={isLoadingAthletes || !!filterAthleteId}
              style={{
                opacity: filterAthleteId ? 0.7 : 1,
                cursor: filterAthleteId ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">No athlete assigned</option>
              {athletes
                .filter(athlete => !filterAthleteId || athlete._id === filterAthleteId)
                .map(athlete => {
                  const plan = (athlete as any).subscription?.plan || 'free';
                  const canSelect = hasPlanOrHigher(plan, 'hs60');
                  return (
                    <option key={athlete._id} value={athlete._id} disabled={!canSelect}>
                      {athlete.firstName} {athlete.lastName}{!canSelect ? ' (Upgrade plan)' : ''}
                    </option>
                  );
                })}
            </Select>
            {filterAthleteId && (
              <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                Equipment will be automatically assigned to you
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={`Enter ${formData.type} name`}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="brand">Brand</Label>
            <Input
              type="text"
              id="brand"
              name="brand"
              value={formData.brand}
              onChange={handleChange}
              placeholder="Enter brand name"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="size">Size / Length / Description</Label>
            <Input
              type="text"
              id="size"
              name="size"
              value={formData.size}
              onChange={handleChange}
              placeholder="Enter size"
            />
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose}>
              <span>Cancel</span>
            </Button>
            <Button type="submit" $variant="primary">
              <span>{isEditing ? 'Update' : 'Add'} {getEquipmentTypeLabel(formData.type)}</span>
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EquipmentForm; 